import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
    ChevronDown,
    ExternalLink,
    MousePointer2,
    Code2,
    Terminal,
    Zap,
} from 'lucide-react';
import { useApiKey } from '@/stores/apiKey.store';
import { showToast } from '@/lib/toast';
import {
    buildCursorUrl,
    buildVscodeSnippet,
    buildClaudeCodeSnippet,
    buildZedUrl,
    buildContextualPrompt,
} from '@/lib/ide-deeplinks';

interface Props {
    method: string;
    path: string;
    summary: string;
}

/**
 * "Buka di IDE" dropdown — exposes deep-link / clipboard-snippet entry
 * points for the four IDEs commonly used by SatuTim users:
 *
 *   - Cursor       — native deep link (`cursor://`)
 *   - VS Code      — clipboard snippet (no reliable prompt-prefill scheme)
 *   - Claude Code  — clipboard cURL for the CLI
 *   - Zed          — best-effort deep link, clipboard fallback on failure
 *
 * For schemes that may not have a handler installed we wrap `window.open`
 * in try/catch and fall back to copying the prompt to clipboard.
 */
export default function OpenInIde({ method, path, summary }: Props) {
    const { key } = useApiKey();

    const input = { method, path, summary, apiKey: key ?? undefined };

    /** Attempt to open a custom-scheme URL; on failure, copy fallback text. */
    async function openSchemeWithFallback(
        url: string,
        fallbackText: string,
        successMsg: string,
        fallbackMsg: string,
    ) {
        try {
            const win = window.open(url, '_blank');
            // If the browser blocked or there is no handler, `win` may be null.
            if (!win) throw new Error('open blocked or no handler');
            showToast(successMsg);
        } catch {
            await copyToClipboard(fallbackText, fallbackMsg);
        }
    }

    async function copyToClipboard(text: string, msg: string) {
        try {
            await navigator.clipboard.writeText(text);
            showToast(msg);
        } catch {
            showToast('Tidak bisa salin — coba lagi atau pakai shortcut manual');
        }
    }

    const onCursor = () => {
        const url = buildCursorUrl(input);
        const fallback = buildContextualPrompt(input);
        openSchemeWithFallback(
            url,
            fallback,
            'Membuka Cursor…',
            'Cursor tidak terdeteksi — prompt tersalin ke clipboard',
        );
    };

    const onVscode = () => {
        const snippet = buildVscodeSnippet(input);
        copyToClipboard(snippet, 'Snippet tersalin — paste di VS Code editor');
    };

    const onClaudeCode = () => {
        const snippet = buildClaudeCodeSnippet(input);
        copyToClipboard(snippet, 'Perintah cURL tersalin — paste di terminal');
    };

    const onZed = () => {
        const url = buildZedUrl(input);
        const fallback = buildContextualPrompt(input);
        if (url) {
            openSchemeWithFallback(
                url,
                fallback,
                'Membuka Zed…',
                'Zed tidak terdeteksi — prompt tersalin ke clipboard',
            );
        } else {
            copyToClipboard(fallback, 'Prompt tersalin untuk Zed');
        }
    };

    const items: Array<{
        key: string;
        label: string;
        hint: string;
        icon: React.ComponentType<{ className?: string }>;
        onSelect: () => void;
    }> = [
        {
            key: 'cursor',
            label: 'Cursor',
            hint: 'Butuh Cursor terinstall',
            icon: MousePointer2,
            onSelect: onCursor,
        },
        {
            key: 'vscode',
            label: 'VS Code',
            hint: 'Salin snippet ke clipboard',
            icon: Code2,
            onSelect: onVscode,
        },
        {
            key: 'claude-code',
            label: 'Claude Code (CLI)',
            hint: 'Salin perintah cURL',
            icon: Terminal,
            onSelect: onClaudeCode,
        },
        {
            key: 'zed',
            label: 'Zed',
            hint: 'Coba buka — fallback ke clipboard',
            icon: Zap,
            onSelect: onZed,
        },
    ];

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-ink transition-colors hover:bg-bg-hover"
                    style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--border)' }}
                    aria-label="Open in IDE menu"
                >
                    <ExternalLink className="h-3 w-3 text-ghost" />
                    <span>Buka di IDE</span>
                    <ChevronDown className="h-3 w-3 opacity-70" />
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    sideOffset={6}
                    className="z-50 min-w-[240px] rounded-lg p-1 shadow-xl"
                    style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <DropdownMenu.Item
                                key={item.key}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    item.onSelect();
                                }}
                                className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs text-ink outline-none cursor-pointer data-[highlighted]:bg-bg-hover transition-colors"
                                style={{ color: 'var(--ink)' }}
                            >
                                <Icon className="h-3.5 w-3.5 text-muted shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium leading-tight">{item.label}</div>
                                    <div className="text-[10px] text-muted leading-tight mt-0.5">
                                        {item.hint}
                                    </div>
                                </div>
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
